import React, { useState, useMemo } from 'react';
import { C, ACCENT } from '../../theme';
import { RefreshCw, Power, AlertTriangle } from 'lucide-react';

// Visual cleanup (2026-10-04) — layout/presentation only, no data/calculation/label-meaning
// change. "Plan value" needs a color distinct from "Client value" (ACCENT) — reuses the violet
// already used elsewhere in this app for a client-ish/ownership concept (Building2 icon color in
// DigitalPlanningEngine.js/AudienceManager), the one new color this pass introduces.
const PLAN_VIOLET = '#7030A0';
// Existing design-system danger/text token (theme.js semantic colors) — not a new color.
const DANGER = '#a02020';
const GROUP_BAND_HEIGHT = 26;
const ROW_HEIGHT = 40;

// "standard_display" -> "Standard display". Display-only — never applied to a value before it's
// read back into form state/patches.
function humanize(s) {
  if (s==null || s==='') return s;
  return String(s).replace(/_/g,' ').replace(/^./, c=>c.toUpperCase());
}

// Shared catalogue table. Step 1 (2026-10-04): wired to the Global Platforms & Formats page
// (scope='global'). Step 2 (2026-10-04): wired to the per-client Catalogue tab (scope='client').
// Step 3 (plan drawer) not wired yet.
//
// `persist({isNew, id, data}) => Promise` is the single write path for both the edit modal and
// bulk import. For scope='global', `data` is the full platform object/patch (PATCH/POST
// /platforms). For scope='client', `data` is an override-patch already using Client.catalogue_
// overrides' own key names (see CLIENT_OVERRIDE_KEY_MAP below) — the caller PATCHes
// /clients/{id}/catalogue-overrides with it.

// Client.catalogue_overrides key-name mapping — a handful of override keys differ from the live
// field name (legacy naming from the original client-override feature, predating this shared
// component); everything else is same-named. Shared by this component's client-scope persist/
// import AND by DigitalPlanningEngine.js's drawer client-scope button (_mapClientOvr/updP import
// this), so both screens always read/write the identical JSON shape.
export const CLIENT_OVERRIDE_KEY_MAP = { base_cpm:'cpm_eur', cpc:'cpc_eur', ctr:'default_ctr', vtr:'default_vtr', weekly_frq:'default_weekly_frq', monthly_frq:'default_monthly_frq' };
export function fieldKeyToOverrideKey(k){ return CLIENT_OVERRIDE_KEY_MAP[k] || k; }
export function overrideKeyToFieldKey(k){ const e = Object.entries(CLIENT_OVERRIDE_KEY_MAP).find(([,v])=>v===k); return e ? e[0] : k; }
export function mapOverrideToFields(ovr){ if(!ovr)return{}; const out={}; Object.entries(ovr).forEach(([k,v])=>{out[overrideKeyToFieldKey(k)]=v;}); return out; }
// The only fields a client is allowed to override — identity fields (type/format_group/
// reach_pool/site/device/overlap_group) and name/seller are never client-overridable, read-only
// in the client-scope edit modal.
export const CLIENT_OVERRIDABLE_FIELDS = ['base_cpm','cpc','base_discount','seasonal_cpm','viewability','ctr','vtr','engagement_rate','cvr','weekly_frq','monthly_frq','audience_comp','real_users_coef','weekly_impression_cap','monthly_impression_cap','active'];

// Found 2026-10-04 (step 3a): DigitalPlanningEngine.js's own catalogue-merge `base` object applies
// per-field fallback defaults using `||`, not `??` — e.g. `vtr:p.vtr||null` silently collapses a
// legitimate `vtr=0` (a real, valid value for some formats) to `null`. A "live value" comparison
// that reads the raw global Platform row directly (no normalization) disagrees with the engine's
// own merged value for exactly these fields, producing a false "differs from current catalogue"
// on a platform nobody ever touched. This mirrors that exact same per-field fallback so client/
// plan "live value" comparisons always agree with what the engine's own merge would produce.
export function normalizeGlobalPlatform(p) {
  return {
    base_cpm: p.base_cpm||0, cpc: p.cpc||null, base_discount: p.base_discount??null,
    viewability: p.viewability||0.7, audience_comp: p.audience_comp||0.65, real_users_coef: p.real_users_coef||2.0,
    ctr: p.ctr||0.001, vtr: p.vtr||null, engagement_rate: p.engagement_rate??null,
    weekly_frq: p.weekly_frq||4, monthly_frq: p.monthly_frq||16, reach_pool: p.reach_pool||'display',
    active: p.active!==false, seasonal_cpm: p.seasonal_cpm||null, cvr: p.cvr||null,
    site_id: p.site_id||null, device: p.device||null, format_overlap_group: p.format_overlap_group||null,
    weekly_impression_cap: p.weekly_impression_cap||null, monthly_impression_cap: p.monthly_impression_cap||null,
  };
}

// Column order is the literal spec order — do not reorder without updating both the table render
// and the Excel template below, which must stay in lockstep. `publisher` group columns (Site/
// Device/Overlap Group) only apply to orgs with real sites (plus super admins) — gated by the
// `showPublisherCols` prop everywhere: table, modal, column picker, Excel template and import.
// width/align/numeric are presentation-only (fixed widths so Global/Client/Plan line up
// identically; numeric columns right-aligned with tabular numerals and a fixed decimal count).
// `fullLabel` is the header tooltip for any column label short enough to risk ambiguity.
const CATALOGUE_COLS = [
  {key:'name',      label:'Platform / Format', group:'identity', width:170, align:'left', sticky:true},
  {key:'seller',    label:'Seller',            group:'identity', width:100, align:'left'},
  {key:'type',      label:'Type',              group:'identity', width:88,  align:'left'},
  {key:'format_group', label:'Format Group',   group:'format',   width:134, align:'left', humanize:true},
  {key:'reach_pool',   label:'Reach Pool',     group:'format',   width:90,  align:'left', humanize:true},
  {key:'site_id',      label:'Site',           group:'publisher',width:90,  align:'left'},
  {key:'device',       label:'Device',         group:'publisher',width:84,  align:'left', humanize:true},
  {key:'format_overlap_group', label:'Overlap Grp', fullLabel:'Overlap Group', group:'publisher', width:114, align:'left', humanize:true},
  {key:'base_cpm',  label:'CPM €',             group:'pricing', width:70, align:'right', numeric:true},
  {key:'cpc',       label:'CPC €',             group:'pricing', width:92, align:'right', numeric:true},
  {key:'base_discount', label:'Discount', fullLabel:'Base Discount', group:'pricing', width:76, align:'right', numeric:true},
  {key:'seasonal_cpm',  label:'Seasonal CPM',  group:'pricing', width:96, align:'left'},
  {key:'viewability', label:'Viewab.', fullLabel:'Viewability', group:'performance', width:72, align:'right', numeric:true},
  {key:'ctr',          label:'CTR%',           group:'performance', width:62, align:'right', numeric:true},
  {key:'vtr',          label:'VTR%',           group:'performance', width:58, align:'right', numeric:true},
  {key:'engagement_rate', label:'Engagem.', fullLabel:'Engagement Rate', group:'performance', width:78, align:'right', numeric:true},
  {key:'cvr',             label:'CVR',             group:'performance', width:62, align:'right', numeric:true},
  {key:'weekly_frq',  label:'Wkly FRQ', fullLabel:'Weekly FRQ', group:'frequency', width:68, align:'right', numeric:true},
  {key:'monthly_frq', label:'Mthly FRQ', fullLabel:'Monthly FRQ (auto)', group:'frequency', width:88, align:'right', numeric:true},
  {key:'audience_comp',  label:'Aud. Comp.', fullLabel:'Audience Comp.', group:'audience', width:80, align:'right', numeric:true},
  {key:'real_users_coef', label:'RUC',           group:'audience', width:56, align:'right', numeric:true},
  {key:'weekly_impression_cap',  label:'Wkly Cap', fullLabel:'Weekly Impression Cap',  group:'caps', width:92, align:'right', numeric:true},
  {key:'monthly_impression_cap', label:'Mthly Cap', fullLabel:'Monthly Impression Cap', group:'caps', width:92, align:'right', numeric:true},
  {key:'active', label:'Active', group:'status', width:90, align:'left'},
];
const GROUP_BAND_LABELS = { identity:'Identity', format:'Format Info', publisher:'Publisher Reach', pricing:'Pricing', performance:'Performance', frequency:'Frequency', audience:'Audience & Reach', caps:'Volume Caps', status:'Status' };
const TOGGLEABLE_GROUPS = [
  {key:'format',      label:'Format Info'},
  {key:'publisher',   label:'Publisher Reach'},
  {key:'pricing',     label:'Pricing'},
  {key:'performance', label:'Performance'},
  {key:'frequency',   label:'Frequency'},
  {key:'audience',    label:'Audience & Reach'},
  {key:'caps',        label:'Volume Caps'},
];
const ALWAYS_ON_GROUPS = ['identity','status'];
const ALL_GROUPS_DEFAULT = TOGGLEABLE_GROUPS.map(g=>g.key); // nothing hidden by default — matches today's always-shown behavior
const LS_KEY = 'dpe_catalogue_table_cols';
function loadVisibleGroups(){try{const s=localStorage.getItem(LS_KEY);if(s)return JSON.parse(s);}catch(e){}return ALL_GROUPS_DEFAULT;}

const AD_TYPES = [['banner','Banner'],['video','Video'],['branding','Branding'],['nonstandard','Non-Standard'],['social','Social'],['sem','SEM']];

// Reused by both the single-row edit modal validation UX and the Excel import. Blank = unchanged
// (caller omits the field from its patch entirely — never coerced to zero/null); a present but
// invalid cell (<=0, or non-numeric) is rejected and the row is named, never silently written.
export function parseNumericCell(raw) {
  if (raw === null || raw === undefined || raw === '') return { value: null, error: null };
  const v = Number(raw);
  if (!Number.isFinite(v) || v <= 0) return { value: null, error: 'must be a positive number (blank = unchanged)' };
  return { value: v, error: null };
}
// Backward-compatible alias — this is the exact function Step 2 (Volume Caps bulk import) added
// under this name; Step 1 of the catalogue-parity work generalizes it to every numeric column.
export const parseCapCell = parseNumericCell;

// Equality used by client-scope import's "a cell becomes a client value only if it differs from
// the global value" rule — numeric tolerance for floats, deep-equal for the seasonal_cpm array.
export function valuesEqual(a, b) {
  if (Array.isArray(a) || Array.isArray(b)) return JSON.stringify(a||null) === JSON.stringify(b||null);
  if (typeof a === 'number' && typeof b === 'number') return Math.abs(a-b) < 1e-9;
  return (a??null) === (b??null);
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const TYPE_MAP = {'banner standard':'banner','billboard':'banner','fluid skin':'banner','interscroller':'banner','pause banner':'banner','intext':'banner','in-text':'banner','feed static':'banner','multiformat':'social','sponsored content':'social','reels video':'social','in-stream':'video','out-stream':'video','yt_skippable':'video','yt_non-skippable':'video','skippable':'video','non-skippable':'video','rich media':'nonstandard','non-standard':'nonstandard','fullscreen':'nonstandard','branding takeover':'branding','search text ad':'sem','performance max':'sem'};

function seasonalSummary(sc) {
  if (!sc || !Array.isArray(sc) || sc.every(v=>v===1||v==null)) return null;
  const min = Math.min(...sc), max = Math.max(...sc);
  return min===max ? `×${min.toFixed(2)}` : `×${min.toFixed(2)}–${max.toFixed(2)}`;
}

function Modal({ title, onClose, children, width=580 }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(80,120,160,0.25)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }} onClick={onClose}>
      <div style={{...C.modal, width}} onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:800, color:'#1a2a3a' }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#8aa0b8', fontSize:22, cursor:'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Deactivate confirm/refusal dialog (global scope only, 2026-10-05) — `DELETE /platforms/{id}` is
// a soft deactivation (active=false), never a real row delete; it 400s with a structured reference
// list (plans/clients/tracker-or-audience entries) when the platform is still in use, which this
// dialog renders in place of the plain confirm. A super admin additionally gets a "Force
// deactivate anyway" button, which retries with `force=true` (403 for every other role).
function DeactivateDialog({ platform, isSuperAdmin, onClose, onConfirm }) {
  const [blocked, setBlocked] = useState(null); // null = plain confirm; object = refused, shows the list
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function run(force) {
    setBusy(true); setError('');
    try {
      await onConfirm(platform, force);
      onClose();
    } catch (e) {
      if (e.detail && typeof e.detail === 'object' && e.detail.blocked) setBlocked(e.detail);
      else setError(e.message || 'Deactivate failed');
    } finally {
      setBusy(false);
    }
  }

  function RefList({ title, items, more, renderItem }) {
    if (!items.length) return null;
    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#8aa0b8', textTransform: 'uppercase', marginBottom: 4 }}>{title}</div>
        {items.map((it, i) => <div key={i} style={{ fontSize: 12, color: '#1a2a3a', padding: '2px 0' }}>{renderItem(it)}</div>)}
        {more > 0 && <div style={{ fontSize: 11, color: '#8aa0b8', fontStyle: 'italic' }}>+{more} more</div>}
      </div>
    );
  }

  return (
    <Modal title={blocked ? 'Cannot deactivate' : `Deactivate "${platform.name}"?`} onClose={onClose} width={480}>
      {!blocked ? (
        <>
          <p style={{ fontSize: 13, color: '#4a6a8a', marginBottom: 18 }}>
            This hides it from new plans and the catalogue. Plans that already used it keep their saved copy — nothing already saved changes.
          </p>
          {error && <div style={{ ...C.feedback('danger'), marginBottom: 14 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button style={C.btnSecondary} onClick={onClose} disabled={busy}>Cancel</button>
            <button style={C.btnDanger} onClick={() => run(false)} disabled={busy}>{busy ? 'Deactivating…' : 'Deactivate'}</button>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, color: '#a02020' }}>
            <AlertTriangle size={16} />
            <span style={{ fontWeight: 700, fontSize: 13 }}>Cannot deactivate: still used by</span>
          </div>
          <RefList title="Plans" items={blocked.plans} more={blocked.plans_more} renderItem={p => <>{p.name} {p.client_name ? `(${p.client_name})` : ''} — <span style={C.mutedLabel()}>{p.status}</span></>} />
          <RefList title="Clients" items={blocked.clients} more={blocked.clients_more} renderItem={c => c.name} />
          <RefList title="Trackers / audiences" items={blocked.tracker_or_audience_entries} more={blocked.tracker_or_audience_more} renderItem={e => <>{e.type === 'tracker' ? 'Tracker: ' : 'Audience: '}{e.name}</>} />
          {blocked.hidden_other_org_references > 0 && (
            <div style={{ fontSize: 11, color: '#8aa0b8', marginBottom: 10 }}>+ {blocked.hidden_other_org_references} reference(s) in other organizations (hidden)</div>
          )}
          {error && <div style={{ ...C.feedback('danger'), marginBottom: 14 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button style={C.btnSecondary} onClick={onClose} disabled={busy}>Cancel</button>
            {isSuperAdmin && <button style={C.btnDanger} onClick={() => run(true)} disabled={busy} title="Deactivates anyway and logs the override">{busy ? 'Deactivating…' : 'Force deactivate anyway'}</button>}
          </div>
        </>
      )}
    </Modal>
  );
}

// Plan scope's 3-way cell state (step 3a, 2026-10-04), computed LIVE every call — never from
// `_has_plan_override` (set unconditionally for every saved platform, so it can't distinguish
// "inherited" from "deliberately overridden"). Re-merges what a brand-new plan would see today
// (client override if the client currently has one for this field, else the live global value)
// and compares it against this plan's own frozen value:
//   frozen === live, no client override -> 'global'
//   frozen === live, client override set -> 'client'
//   frozen !== live -> 'plan' (covers both "the planner deliberately changed it" and "the global/
//     client catalogue moved since this plan was saved" — same comparison either way)
// Exported standalone so this exact comparison is unit-testable without rendering JSX.
export function computePlanCellState(entry, fieldKey, globalPlatform) {
  const ovrFields = mapOverrideToFields(entry._client_overrides);
  const hasClientOverride = Object.prototype.hasOwnProperty.call(ovrFields, fieldKey);
  const live = hasClientOverride ? ovrFields[fieldKey] : globalPlatform[fieldKey];
  const frozen = entry[fieldKey];
  return { state: valuesEqual(frozen, live) ? (hasClientOverride ? 'client' : 'global') : 'plan', live, hasClientOverride };
}

// Refresh-from-catalogue diff rows (step 3b, 2026-10-04) — same "differs from current catalogue"
// comparison as computePlanCellState/getSourceState above, just collected across every
// CLIENT_OVERRIDABLE_FIELDS for one platform (platformDbId given) or every platform (omitted).
// `deliberate` is `plan.data.catalogue_deliberate` — {platformDbId (string): [fieldKey, ...]} — a
// row whose field is in that list defaults UNTICKED ("Set in this plan"); everything else starts
// ticked. Pure and exported so the diff dialog's row set is unit-testable without rendering JSX.
export function computeRefreshDiff(catalogue, globalByDbId, deliberate, platformDbId) {
  const rows = [];
  (catalogue || []).forEach(p => {
    if (platformDbId != null && p._db_id !== platformDbId) return;
    const g = globalByDbId?.[p._db_id];
    if (!g) return;
    const deliberateFields = (deliberate && deliberate[String(p._db_id)]) || [];
    CLIENT_OVERRIDABLE_FIELDS.forEach(fieldKey => {
      const { state, live, hasClientOverride } = computePlanCellState(p, fieldKey, g);
      if (state !== 'plan') return;
      rows.push({
        platformDbId: p._db_id,
        platformName: p.name || p.label,
        field: fieldKey,
        frozenValue: p[fieldKey],
        liveValue: live,
        source: hasClientOverride ? 'client' : 'global',
        deliberate: deliberateFields.includes(fieldKey),
      });
    });
  });
  return rows;
}

// Single source of truth for a cell's provenance, shared by the inline dot/icon markers and the
// optional "Show sources" text caption below — client scope is the existing 2-way (global/
// client), plan scope the 3-way (global/client/plan) from computePlanCellState above. Returns
// null for global scope or a non-overridable field (identity columns): nothing to mark there.
function getSourceState(scope, p, fieldKey, globalByDbId) {
  if (!CLIENT_OVERRIDABLE_FIELDS.includes(fieldKey)) return null;
  if (scope === 'client') {
    const ovrKey = fieldKeyToOverrideKey(fieldKey);
    const hasOverride = !!(p._client_overrides && Object.prototype.hasOwnProperty.call(p._client_overrides, ovrKey));
    return { source: hasOverride ? 'client' : 'global', differs: false, live: null };
  }
  if (scope === 'plan') {
    const g = globalByDbId?.[p._db_id];
    if (!g) return null;
    const { state, live } = computePlanCellState(p, fieldKey, g);
    return { source: state, differs: state === 'plan', live };
  }
  return null;
}

// Default state: one line, no caption — a plain value means "Global value" (no dot, no red). A
// source dot sits in a FIXED 12px slot before the value (reserved on every markable cell whether
// or not a dot renders this row — the number's right edge and the text's left edge must not shift
// depending on whether this particular row happens to have one): accent blue = Client value,
// violet = Plan value. "Plan value — differs from current catalogue" (2026-10-04, 2nd visual pass:
// replaces the earlier amber triangle icon) turns the VALUE TEXT itself danger-red instead — no
// icon, no bold — with the live value on hover. Every cell gets a tooltip naming its source.
// "Show sources" (off by default) also prints the text label below; the red value stays red either way.
function SourceMarkers({ scope, p, fieldKey, onResetField, globalByDbId, showSources, children }) {
  const st = getSourceState(scope, p, fieldKey, globalByDbId);
  if (!st) return children;
  const dotColor = st.source==='client' ? ACCENT : st.source==='plan' ? PLAN_VIOLET : null;
  const sourceLabel = st.source==='client' ? 'Client value' : st.source==='plan' ? 'Plan value' : 'Global value';
  const canReset = st.source === 'plan' || (scope === 'client' && st.source === 'client');
  const liveLabel = st.live!=null ? (Array.isArray(st.live) ? `[${st.live.join(', ')}]` : String(st.live)) : null;
  const reset = canReset ? (e=>{e.stopPropagation(); onResetField && onResetField(p._db_id, fieldKey);}) : undefined;
  const titleText = st.differs ? `Differs from current catalogue — live value: ${liveLabel}` : sourceLabel;
  return (
    <>
      <span style={{display:'flex',alignItems:'center'}} title={titleText}>
        <span onClick={reset} style={{width:12,minWidth:12,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,cursor:canReset?'pointer':'default'}}>
          {dotColor && <span style={{width:6,height:6,minWidth:6,borderRadius:'50%',background:dotColor}}/>}
        </span>
        <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:st.differs?DANGER:undefined}}>{children}</span>
      </span>
      {showSources && (
        <div onClick={reset} style={{fontSize:9,color:st.differs?DANGER:(dotColor||'#8aa0b8'),cursor:canReset?'pointer':'default',whiteSpace:'nowrap'}}>
          {sourceLabel}{st.differs?' — differs':''}
        </div>
      )}
    </>
  );
}

// max_weekly_frequency isn't one of the 24 listed/ordered table columns, but it's a real, actively
// used field (planner frequency-warning threshold, see MAX_FREQ_DEFAULT call sites in
// DigitalPlanningEngine.js) that the old page's modal + Excel template both covered — kept in the
// modal and Excel template (appended, not part of the specified 24-column order) so this redesign
// doesn't quietly regress an unrelated field's editability. Not client-overridable (not in
// CLIENT_OVERRIDABLE_FIELDS), consistent with it being absent from the client-override spec.
const BLANK = { name:'', seller:'', type:'banner', format_group:'', base_cpm:0, cpc:null, base_discount:null, seasonal_cpm:null, viewability:0.7, ctr:0, vtr:0, cvr:null, engagement_rate:null, weekly_frq:3, monthly_frq:10, max_weekly_frequency:null, audience_comp:0.65, real_users_coef:2.0, reach_pool:'other', weekly_impression_cap:null, monthly_impression_cap:null, site_id:null, device:'', format_overlap_group:'' };

export default function CatalogueTable({ scope, platforms, sites, canEdit, persist, onReload, showPublisherCols, globalByDbId, onResetField, onResetAll, readOnlyReason, onRefreshRow, onRefreshAll, refreshablePlatformIds, showInactive, onToggleShowInactive, onDeactivate, onReactivate, isSuperAdmin }) {
  const nonGlobalScope = scope==='client' || scope==='plan';
  const editsDisabled = !!readOnlyReason;
  const [search, setSearch] = useState('');
  const [visGroups, setVisGroups] = useState(loadVisibleGroups);
  const [groupModal, setGroupModal] = useState(false);
  const [modal, setModal] = useState(null); // null | 'new' | platform being edited
  const [form, setForm] = useState(BLANK);
  const [tab, setTab] = useState('CPM & Pricing');
  const [saving, setSaving] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkVariant, setBulkVariant] = useState('info');
  const [bulkErrors, setBulkErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null); // global scope only

  function toggleGroup(key){
    setVisGroups(prev=>{
      const next = prev.includes(key)?prev.filter(k=>k!==key):[...prev,key];
      try{localStorage.setItem(LS_KEY, JSON.stringify(next));}catch(e){}
      return next;
    });
  }
  function resetGroups(){
    setVisGroups(ALL_GROUPS_DEFAULT);
    try{localStorage.setItem(LS_KEY, JSON.stringify(ALL_GROUPS_DEFAULT));}catch(e){}
  }

  const groupPickerGroups = useMemo(()=>TOGGLEABLE_GROUPS.filter(g=>g.key!=='publisher'||showPublisherCols), [showPublisherCols]);
  const visibleCols = useMemo(()=>CATALOGUE_COLS.filter(c=>{
    if (c.group==='publisher' && !showPublisherCols) return false;
    return ALWAYS_ON_GROUPS.includes(c.group)||visGroups.includes(c.group);
  }), [visGroups, showPublisherCols]);
  // Consecutive same-group runs among the currently visible columns, for the group-band row —
  // one <th colSpan> per run, matching the column picker's own grouping.
  const groupRuns = useMemo(()=>{
    const runs = [];
    visibleCols.forEach(c=>{
      const last = runs[runs.length-1];
      if (last && last.group===c.group) last.count++;
      else runs.push({group:c.group, count:1});
    });
    return runs;
  }, [visibleCols]);

  const filtered = useMemo(()=>{
    const s = search.toLowerCase();
    if (!s) return platforms;
    return platforms.filter(p =>
      p.name.toLowerCase().includes(s) ||
      (p.seller||'').toLowerCase().includes(s) ||
      (p.type||'').toLowerCase().includes(s)
    );
  }, [platforms, search]);

  const [formInitial, setFormInitial] = useState(BLANK);
  function openNew() { setForm({...BLANK}); setFormInitial({...BLANK}); setTab('CPM & Pricing'); setModal('new'); }
  function openEdit(p) {
    const next = {
      name:p.name, seller:p.seller||'', type:p.type||'banner', format_group:p.format_group||'',
      base_cpm:p.base_cpm||0, cpc:p.cpc??null, base_discount:p.base_discount??null, seasonal_cpm:p.seasonal_cpm||null,
      viewability:p.viewability||0.7, ctr:p.ctr||0, vtr:p.vtr||0, cvr:p.cvr??null, engagement_rate:p.engagement_rate??null,
      weekly_frq:p.weekly_frq||3, monthly_frq:p.monthly_frq||10, max_weekly_frequency:p.max_weekly_frequency??null,
      audience_comp:p.audience_comp||0.65, real_users_coef:p.real_users_coef||2.0, reach_pool:p.reach_pool||'other',
      weekly_impression_cap:p.weekly_impression_cap??null, monthly_impression_cap:p.monthly_impression_cap??null,
      site_id:p.site_id||null, device:p.device||'', format_overlap_group:p.format_overlap_group||'',
    };
    setForm(next); setFormInitial(next); setTab('CPM & Pricing'); setModal(p);
  }

  // For client/plan scope, only send a patch for a field that actually CHANGED during this modal
  // session (diffed against formInitial, the values the modal opened with) — not every overridable
  // field unconditionally. `form` always carries every field's current (possibly just-inherited)
  // value for display, so a blanket send would turn every untouched field into a frozen override
  // too — client-side instance of the exact same bug shape the step-2/3a backend deep-merges guard
  // against. Client scope key-maps to Client.catalogue_overrides' own names; plan scope doesn't
  // (the plan's frozen catalogue entry uses the same field names as the live merge).
  function buildSavePayload() {
    if (scope === 'client') {
      const patch = {};
      CLIENT_OVERRIDABLE_FIELDS.forEach(k => { if (!valuesEqual(form[k], formInitial[k])) patch[fieldKeyToOverrideKey(k)] = form[k]; });
      return patch;
    }
    if (scope === 'plan') {
      const patch = {};
      CLIENT_OVERRIDABLE_FIELDS.forEach(k => { if (!valuesEqual(form[k], formInitial[k])) patch[k] = form[k]; });
      return patch;
    }
    return form;
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (modal==='new') await persist({isNew:true, id:null, data:form});
      else await persist({isNew:false, id: nonGlobalScope ? modal._db_id : modal.id, data: buildSavePayload()});
      onReload(); setModal(null);
    } catch(e){ alert(e.message); } finally{ setSaving(false); }
  }

  const clientRO = (key) => nonGlobalScope && !CLIENT_OVERRIDABLE_FIELDS.includes(key);
  const f = (key, label, type='number', step=0.01) => {
    const ro = clientRO(key);
    return (
      <label style={{display:'block', marginBottom:14}}>
        <span style={C.label}>{label}</span>
        <input type={type} step={step} value={form[key]} readOnly={ro}
          onChange={e=>{ if(ro) return; setForm(p=>({...p,[key]:type==='number'?+e.target.value:e.target.value})); }}
          style={{...C.input, ...(ro?{background:'rgba(220,232,240,0.4)', color:'#8aa0b8', cursor:'not-allowed'}:{})}}/>
      </label>
    );
  };

  async function handleBulkImport(e) {
    const file = e.target.files[0]; if (!file) return;
    setImporting(true); setBulkStatus('Parsing…'); setBulkVariant('info'); setBulkErrors([]);
    let XLSX;
    try { XLSX = await import('xlsx'); } catch(err) {}
    if (!XLSX) { setBulkStatus('Excel library not available'); setBulkVariant('danger'); setImporting(false); return; }
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type:'array' });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { header:1, defval:'' });
    const errs = []; let updated = 0;
    if (rows.length < 2) { setBulkStatus('Empty template'); setBulkVariant('warning'); setImporting(false); e.target.value=''; return; }

    // Header-name-mapped, not positional — a column's absence from the file means "never touched",
    // not "blank" (the two happen to be handled identically here, but this makes the sheet
    // resilient to a reordered/trimmed-down copy of the template).
    const header = rows[0].map(h=>String(h||'').trim());
    const colIdx = {}; header.forEach((h,i)=>{ colIdx[h]=i; });
    const get = (row, name) => {
      const i = colIdx[name]; if (i==null) return null;
      const v = row[i];
      return (v===null||v===undefined||v==='') ? null : v;
    };
    function findPlat(name) {
      if (!name) return null;
      const n = String(name).toLowerCase().trim();
      return platforms.find(p => p.name.toLowerCase().trim() === n);
    }
    function numField(row, colName, patch, fieldKey, rowNum, label) {
      const raw = get(row, colName);
      if (raw===null) return;
      const {value, error} = parseNumericCell(raw);
      if (error) errs.push(`Row ${rowNum} "${label||colName}" (${raw}) ${error}`);
      else patch[fieldKey] = value;
    }

    const toSave = [];
    rows.slice(1).forEach((row, ri) => {
      const rowNum = ri+2;
      const plat = findPlat(get(row,'Platform'));
      if (!plat) { if (get(row,'Platform')) errs.push(`Row ${rowNum}: "${get(row,'Platform')}" not matched`); return; }
      const patch = {};
      const seller = get(row,'Seller'); if (seller!==null) patch.seller = String(seller);
      const type = get(row,'Type'); if (type!==null) { const raw=String(type).toLowerCase().trim(); patch.type = TYPE_MAP[raw]||(AD_TYPES.some(([k])=>k===raw)?raw:'banner'); }
      const fg = get(row,'Format Group'); if (fg!==null) patch.format_group = String(fg);
      const pool = get(row,'Reach Pool'); if (pool!==null) patch.reach_pool = String(pool).toLowerCase().trim();
      if (showPublisherCols) {
        const site = get(row,'Site'); if (site!==null) { const n=Number(site); if (!Number.isFinite(n)||n<=0) errs.push(`Row ${rowNum} "Site" (${site}) must be a positive numeric site id (blank = external/unchanged)`); else patch.site_id = n; }
        const dev = get(row,'Device'); if (dev!==null) patch.device = String(dev).trim()||null;
        const grp = get(row,'Overlap Group'); if (grp!==null) patch.format_overlap_group = String(grp).trim()||null;
      }
      numField(row,'CPM (EUR)',patch,'base_cpm',rowNum);
      numField(row,'CPC (EUR)',patch,'cpc',rowNum);
      numField(row,'Base Discount',patch,'base_discount',rowNum);
      const months = MONTHS.map(m=>get(row,m));
      if (months.some(v=>v!==null)) {
        const existing = plat.seasonal_cpm || Array(12).fill(1);
        const merged = [...existing]; let monthErr=false;
        months.forEach((raw,i)=>{
          if (raw===null) return;
          const {value,error} = parseNumericCell(raw);
          if (error) { errs.push(`Row ${rowNum} "${MONTHS[i]}" (${raw}) ${error}`); monthErr=true; }
          else merged[i]=value;
        });
        if (!monthErr) patch.seasonal_cpm = merged;
      }
      numField(row,'Viewability',patch,'viewability',rowNum);
      numField(row,'CTR',patch,'ctr',rowNum);
      numField(row,'VTR',patch,'vtr',rowNum);
      numField(row,'Engagement Rate',patch,'engagement_rate',rowNum);
      numField(row,'CVR',patch,'cvr',rowNum);
      numField(row,'Weekly FRQ',patch,'weekly_frq',rowNum);
      // Monthly FRQ (auto) is read-only/derived — never imported, even if present in the file.
      numField(row,'Audience Comp',patch,'audience_comp',rowNum);
      numField(row,'RUC',patch,'real_users_coef',rowNum);
      numField(row,'Weekly Impression Cap',patch,'weekly_impression_cap',rowNum);
      numField(row,'Monthly Impression Cap',patch,'monthly_impression_cap',rowNum);
      const active = get(row,'Active'); if (active!==null) patch.active = String(active).toLowerCase().trim()!=='no';
      if (scope==='global') {
        const maxWfrq = get(row,'Max Weekly FRQ (cap)'); if (maxWfrq!==null) { const {value,error}=parseNumericCell(maxWfrq); if (error) errs.push(`Row ${rowNum} "Max Weekly FRQ (cap)" (${maxWfrq}) ${error}`); else patch.max_weekly_frequency=value; }
      }

      if (scope==='client') {
        // A cell becomes a client override only if it differs from the live global value, and
        // only for fields a client is actually allowed to override — identity/name/seller cells
        // in the sheet are reference-only for client scope, never written even if present.
        const globalP = globalByDbId?.[plat._db_id];
        const overridePatch = {};
        Object.entries(patch).forEach(([fk, v]) => {
          if (!CLIENT_OVERRIDABLE_FIELDS.includes(fk)) return;
          if (globalP && valuesEqual(v, globalP[fk])) return;
          overridePatch[fieldKeyToOverrideKey(fk)] = v;
        });
        if (Object.keys(overridePatch).length) toSave.push({plat, patch: overridePatch, rowNum});
      } else if (scope==='plan') {
        // A cell becomes a plan value only if it differs from what the client/global levels give
        // TODAY (client override if the client currently has one for this field, else the live
        // global value) — same "differs from the live merge" comparison the cell captions use.
        const globalP = globalByDbId?.[plat._db_id];
        const ovrFields = mapOverrideToFields(plat._client_overrides);
        const planPatch = {};
        Object.entries(patch).forEach(([fk, v]) => {
          if (!CLIENT_OVERRIDABLE_FIELDS.includes(fk)) return;
          const live = Object.prototype.hasOwnProperty.call(ovrFields, fk) ? ovrFields[fk] : globalP?.[fk];
          if (live !== undefined && valuesEqual(v, live)) return;
          planPatch[fk] = v;
        });
        if (Object.keys(planPatch).length) toSave.push({plat, patch: planPatch, rowNum});
      } else {
        if (Object.keys(patch).length) toSave.push({plat, patch, rowNum});
      }
    });

    for (const {plat, patch, rowNum} of toSave) {
      try { await persist({isNew:false, id: nonGlobalScope ? plat._db_id : plat.id, data:patch}); updated++; }
      catch(err) { errs.push(`Row ${rowNum}: failed to save "${plat.name}"`); }
    }
    onReload();
    setBulkErrors(errs);
    setBulkVariant(errs.length ? 'warning' : 'success');
    setBulkStatus(`Updated ${updated} platform(s)${errs.length ? ' with '+errs.length+' issue(s)' : ''}`);
    setImporting(false);
    e.target.value = '';
  }

  async function downloadTemplate() {
    let XLSX;
    try { XLSX = await import('xlsx'); } catch(e) {}
    if (!XLSX) { alert('Excel library not available.'); return; }
    const header = ['Platform','Seller','Type','Format Group','Reach Pool',
      ...(showPublisherCols?['Site','Device','Overlap Group']:[]),
      'CPM (EUR)','CPC (EUR)','Base Discount',...MONTHS,'Viewability','CTR','VTR','Engagement Rate','CVR','Weekly FRQ','Monthly FRQ (auto)','Audience Comp','RUC','Weekly Impression Cap','Monthly Impression Cap','Active',
      ...(scope==='global'?['Max Weekly FRQ (cap)']:[]),
    ];
    const rows = [header];
    platforms.forEach(p => {
      const sc = p.seasonal_cpm || Array(12).fill('');
      rows.push([
        p.name, p.seller||'', p.type||'', p.format_group||'', p.reach_pool||'',
        ...(showPublisherCols?[p.site_id||'', p.device||'', p.format_overlap_group||'']:[]),
        p.base_cpm||0, p.cpc??'', p.base_discount??'', ...sc,
        p.viewability||0.7, p.ctr||0, p.vtr||0, p.engagement_rate??'', p.cvr??'',
        p.weekly_frq||3, `${p.weekly_frq||3} avg/wk`, p.audience_comp||0.65, p.real_users_coef||2.0,
        p.weekly_impression_cap??'', p.monthly_impression_cap??'', p.active!==false?'Yes':'No',
        ...(scope==='global'?[p.max_weekly_frequency??'']:[]),
      ]);
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), 'Platforms');
    const scopeTitle = scope==='client'?'CLIENT CATALOGUE — BULK TEMPLATE':scope==='plan'?'PLAN CATALOGUE — BULK TEMPLATE':'PLATFORMS GLOBAL DATABASE — BULK TEMPLATE';
    const inst = [
      [scopeTitle],[''],
      ['RULES:'],
      ['- Do NOT change the Platform column — used for matching existing platforms (new platforms are not created by import)'],
      ['- Leave a cell blank to keep its current value unchanged'],
      ['- A numeric cell of 0 or below, or non-numeric, is rejected — the row is named in the import result and NOT written'],
      ['- Monthly FRQ (auto) is read-only — edit Weekly FRQ only'],
      ['- Reach Pool: yt | social | video | display | other'],
      ['- Jan..Dec: monthly CPM multipliers (1.0 = base price)'],
      ...(showPublisherCols?[['- Site: numeric site id (blank = external platform)'],['- Device: desktop | mobile | app (used when Site is set)']]:[]),
      ...(scope==='client'?[['- Values shown are this client\'s EFFECTIVE values (client override if set, else global). A cell becomes a client override only if the value you enter differs from the live global value — identical to global, or blank, both leave the client unchanged.']]
        :scope==='plan'?[['- Values shown are this plan\'s EFFECTIVE (frozen) values. A cell becomes a plan value only if the value you enter differs from what the client/global levels give today — identical to that, or blank, both leave the plan unchanged.']]
        :[['- Max Weekly FRQ (cap): planner warning threshold; leave blank for default (10)']]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(inst), 'Instructions');
    XLSX.writeFile(wb, (scope==='client'?'Client_Catalogue_':scope==='plan'?'Plan_Catalogue_':'Platforms_Global_') + new Date().toISOString().slice(0,10) + '.xlsx');
  }

  // Cell content only — formatting/humanizing, no logic. "—" muted, same alignment as the rest of
  // its column either way. Numeric columns keep the exact same decimal counts as before this pass.
  function cellContent(p, key, siteName) {
    switch(key) {
      case 'name': return <span style={{fontWeight:700}}>{p.name}</span>;
      case 'seller': return p.seller ? <span style={{color:'#6a8aaa'}}>{p.seller}</span> : <span style={{color:'#8aa0b8'}}>—</span>;
      case 'type': return <span style={C.mutedLabel()}>{p.type||'—'}</span>;
      case 'format_group': return p.format_group ? <span title={humanize(p.format_group)}>{humanize(p.format_group)}</span> : <span style={{color:'#8aa0b8'}}>—</span>;
      case 'reach_pool': { const rpLabel = p.reach_pool==='yt' ? 'YouTube' : humanize(p.reach_pool||'other'); return <span title={rpLabel} style={{color:'#6a8aaa'}}>{rpLabel}</span>; }
      case 'site_id': return siteName==='—' ? <span style={{color:'#8aa0b8'}}>—</span> : <span title={siteName}>{siteName}</span>;
      case 'device': return p.device ? <span title={humanize(p.device)}>{humanize(p.device)}</span> : <span style={{color:'#8aa0b8'}}>—</span>;
      case 'format_overlap_group': return p.format_overlap_group ? <span title={humanize(p.format_overlap_group)}>{humanize(p.format_overlap_group)}</span> : <span style={{color:'#8aa0b8'}}>—</span>;
      case 'base_cpm': return <span style={{fontWeight:600}}>€{(p.base_cpm||0).toFixed(2)}</span>;
      case 'cpc': return p.cpc!=null ? `€${p.cpc.toFixed(2)}` : (p.type==='sem'
        ? <span title="CPC missing" style={{color:DANGER,opacity:0.65}}>—</span>
        : <span style={{color:'#8aa0b8'}}>—</span>);
      case 'base_discount': return p.base_discount!=null ? `${Math.round(p.base_discount*100)}%` : <span style={{color:'#8aa0b8'}}>—</span>;
      case 'seasonal_cpm': return seasonalSummary(p.seasonal_cpm) || <span style={{color:'#8aa0b8'}}>default</span>;
      case 'viewability': return `${((p.viewability||0)*100).toFixed(0)}%`;
      case 'ctr': return p.ctr!=null ? `${(p.ctr*100).toFixed(2)}%` : <span style={{color:'#8aa0b8'}}>—</span>;
      case 'vtr': return p.vtr ? `${(p.vtr*100).toFixed(0)}%` : <span style={{color:'#8aa0b8'}}>—</span>;
      case 'engagement_rate': return p.engagement_rate!=null ? `${(p.engagement_rate*100).toFixed(2)}%` : <span style={{color:'#8aa0b8'}}>—</span>;
      case 'cvr': return p.cvr!=null ? `${(p.cvr*100).toFixed(2)}%` : <span style={{color:'#8aa0b8'}}>—</span>;
      case 'weekly_frq': return p.weekly_frq||3;
      case 'monthly_frq': return <span style={{color:'#8aa0b8'}}>{p.weekly_frq||3} avg/wk</span>;
      case 'audience_comp': return `${((p.audience_comp||0)*100).toFixed(0)}%`;
      case 'real_users_coef': return p.real_users_coef||2.0;
      case 'weekly_impression_cap': return p.weekly_impression_cap ? p.weekly_impression_cap.toLocaleString() : <span style={{color:'#8aa0b8'}}>—</span>;
      case 'monthly_impression_cap': return p.monthly_impression_cap ? p.monthly_impression_cap.toLocaleString() : <span style={{color:'#8aa0b8'}}>—</span>;
      case 'active': return <span style={C.statusRow()}><span style={C.statusDot(p.active!==false?'success':'neutral')}/>{p.active!==false?'active':'inactive'}</span>;
      default: return null;
    }
  }

  const thBase = { ...C.th, padding:'6px 10px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', background:'#e8f0f8' };
  // whiteSpace/textOverflow set here (not only on SourceMarkers' inner span) so every cell is
  // single-line even for non-overridable columns, where SourceMarkers returns raw children with
  // no wrapper at all (identity/format/publisher fields never carry a Client/Plan source marker).
  const tdBase = { padding:'0 10px', fontSize:13, color:'#1a2a3a', borderBottom:'1px solid #eef4f8', height:ROW_HEIGHT, verticalAlign:'middle', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' };
  const numStyle = { fontVariantNumeric:'tabular-nums' };

  return (
    <div>
      {readOnlyReason && <div style={{...C.feedback('warning'), marginBottom:16}} title={readOnlyReason}>{readOnlyReason}</div>}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, gap:10, flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:14, alignItems:'center' }}>
          <input placeholder="Search by name, seller or type…" value={search} onChange={e=>setSearch(e.target.value)}
            style={{ padding:'8px 14px', border:'1px solid #c8d8e8', borderRadius:8, fontSize:13, outline:'none', width:260, background:'rgba(255,255,255,0.7)' }}/>
          {scope==='global' && onToggleShowInactive && (
            <label style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:12, color:'#6a8aaa', cursor:'pointer', userSelect:'none' }}>
              <input type="checkbox" checked={!!showInactive} onChange={e=>onToggleShowInactive(e.target.checked)} style={{ accentColor:ACCENT, cursor:'pointer' }}/>
              Show inactive
            </label>
          )}
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <button style={C.btnSecondary} onClick={()=>setGroupModal(true)}>Columns</button>
          <button style={C.btnSecondary} onClick={downloadTemplate}>Export Template</button>
          {canEdit && <>
            <label style={{...C.btnSecondary, cursor: editsDisabled?'not-allowed':'pointer', opacity: editsDisabled?0.5:1}} title={readOnlyReason||undefined}>
              {importing ? 'Importing…' : 'Bulk Import'}
              <input type="file" accept=".xlsx,.xls" style={{display:'none'}} disabled={importing||editsDisabled} onChange={handleBulkImport}/>
            </label>
            {scope==='client' && onResetAll && (
              <button style={C.btnDanger} disabled={editsDisabled} title={readOnlyReason||undefined} onClick={()=>{ if(window.confirm('Reset ALL client overrides for this catalogue? Every platform will revert to its global values.')) onResetAll(); }}>Reset all client values</button>
            )}
            {scope==='plan' && onRefreshAll && (
              <button style={{...C.btnSecondary, display:'inline-flex', alignItems:'center', gap:6}} disabled={editsDisabled||!refreshablePlatformIds?.size} title={readOnlyReason||(!refreshablePlatformIds?.size?'Nothing differs from the current catalogue':'Refresh every platform that differs from the current catalogue')} onClick={onRefreshAll}><RefreshCw size={13}/>Refresh from catalogue{refreshablePlatformIds?.size?` (${refreshablePlatformIds.size})`:''}</button>
            )}
            {scope==='global' && <button style={C.btnPrimary} disabled={editsDisabled} title={readOnlyReason||undefined} onClick={openNew}>+ New Platform</button>}
          </>}
        </div>
      </div>

      {/* Legend + "Show sources" — one line, only meaningful once there's a source to mark */}
      {nonGlobalScope && (
        <div style={{ display:'flex', alignItems:'center', gap:18, marginBottom:10, fontSize:11, color:'#6a8aaa', flexWrap:'wrap' }}>
          <span style={{display:'inline-flex',alignItems:'center',gap:5}}><span style={{width:6,height:6,borderRadius:'50%',background:ACCENT}}/>Client value</span>
          {scope==='plan' && <span style={{display:'inline-flex',alignItems:'center',gap:5}}><span style={{width:6,height:6,borderRadius:'50%',background:PLAN_VIOLET}}/>Plan value</span>}
          {scope==='plan' && <span style={{display:'inline-flex',alignItems:'center',gap:5,color:DANGER}}>Differs from current catalogue</span>}
          <label style={{display:'inline-flex',alignItems:'center',gap:6,marginLeft:'auto',cursor:'pointer',userSelect:'none'}}>
            <input type="checkbox" checked={showSources} onChange={e=>setShowSources(e.target.checked)} style={{accentColor:ACCENT,cursor:'pointer'}}/>
            Show sources
          </label>
        </div>
      )}

      <div style={{ overflowX:'auto', border:'1px solid #dce8f0', borderRadius:12, background:'rgba(255,255,255,0.75)' }}>
        <table style={{ borderCollapse:'separate', borderSpacing:0, tableLayout:'fixed' }}>
          <colgroup>
            {visibleCols.map(c=><col key={c.key} style={{width:c.width}}/>)}
            {canEdit && <col style={{width:64}}/>}
          </colgroup>
          <thead>
            <tr>
              {groupRuns.map((run,i)=>(
                <th key={i} colSpan={run.count} style={{...thBase, position:'sticky', top:0, zIndex:3, height:GROUP_BAND_HEIGHT, fontSize:9, color:'#8aa0b8', textAlign:'left', borderBottom:'1px solid #dce8f0'}}>
                  {GROUP_BAND_LABELS[run.group]||run.group}
                </th>
              ))}
              {canEdit && <th style={{...thBase, position:'sticky', top:0, zIndex:3, height:GROUP_BAND_HEIGHT}}/>}
            </tr>
            <tr>
              {visibleCols.map((c,i)=>(
                <th key={c.key} title={c.fullLabel||c.label}
                  style={{...thBase, position:'sticky', top:GROUP_BAND_HEIGHT, zIndex:c.sticky?4:2, left:c.sticky?0:undefined, textAlign:c.align}}>
                  {c.label}
                </th>
              ))}
              {canEdit && <th style={{...thBase, position:'sticky', top:GROUP_BAND_HEIGHT, zIndex:2}}/>}
            </tr>
          </thead>
          <tbody>
            {filtered.length===0 ? (
              <tr><td colSpan={visibleCols.length+1} style={{...tdBase, height:'auto', padding:48, textAlign:'center', color:'#8aa0b8'}}>
                {search ? 'No platforms match.' : 'No platforms yet.'}
              </td></tr>
            ) : filtered.map(p=>{
              const siteName = p.site_id ? ((sites||[]).find(s=>s.id===p.site_id)?.name || `#${p.site_id}`) : '—';
              return (
                <tr key={p.id} style={{ cursor: (canEdit&&!editsDisabled) ? 'pointer' : 'default' }}
                  onClick={()=>canEdit&&!editsDisabled&&openEdit(p)}
                  onMouseEnter={e=>e.currentTarget.style.background='rgba(0,144,208,0.04)'}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  {visibleCols.map(c=>(
                    <td key={c.key} style={{...tdBase, textAlign:c.align, ...(c.numeric?numStyle:{}), ...(c.sticky?{position:'sticky',left:0,zIndex:1,background:'#ffffff'}:{})}}>
                      <SourceMarkers scope={scope} p={p} fieldKey={c.key} onResetField={editsDisabled?undefined:onResetField} globalByDbId={globalByDbId} showSources={showSources}>
                        {cellContent(p, c.key, siteName)}
                      </SourceMarkers>
                    </td>
                  ))}
                  {canEdit && (
                    <td style={{...tdBase, textAlign:'right'}} onClick={e=>e.stopPropagation()}>
                      <div style={{display:'flex', gap:4, justifyContent:'flex-end'}}>
                        {scope==='plan' && onRefreshRow && refreshablePlatformIds?.has(p._db_id) && (
                          <button style={{...C.btnSecondary, padding:'4px 6px'}} disabled={editsDisabled} title={readOnlyReason||'Refresh from catalogue'} onClick={()=>onRefreshRow(p._db_id)}><RefreshCw size={13}/></button>
                        )}
                        {scope==='global' && onReactivate && p.active===false && (
                          <button style={{...C.btnSecondary, padding:'4px 10px', display:'inline-flex', alignItems:'center', gap:5}} title="Reactivate" onClick={()=>onReactivate(p)}><Power size={13}/>Reactivate</button>
                        )}
                        {scope==='global' && onDeactivate && p.active!==false && (
                          <button style={{...C.btnSecondary, padding:'4px 6px'}} title="Deactivate" onClick={()=>setDeactivateTarget(p)}><Power size={13}/></button>
                        )}
                        <button style={C.btnSecondary} disabled={editsDisabled} title={readOnlyReason||undefined} onClick={()=>openEdit(p)}>Edit</button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {bulkStatus && <div style={{ ...C.feedback(bulkVariant), marginTop:12, marginBottom:0, fontSize:12 }}>{bulkStatus}</div>}
      {bulkErrors.length > 0 && (
        <div style={{ ...C.feedback('warning'), marginTop:8, marginBottom:0, fontSize:11, maxHeight:140, overflowY:'auto' }}>
          {bulkErrors.map((e,i) => <div key={i}>{e}</div>)}
        </div>
      )}

      {groupModal && (
        <Modal title="Visible Column Groups" onClose={()=>setGroupModal(false)} width={420}>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px 24px', marginBottom:20}}>
            {groupPickerGroups.map(g=>(
              <label key={g.key} style={{display:'flex', alignItems:'center', gap:8, fontSize:12, color:'#1a2a3a', cursor:'pointer', userSelect:'none'}}>
                <input type="checkbox" checked={visGroups.includes(g.key)} onChange={()=>toggleGroup(g.key)} style={{width:14,height:14,accentColor:ACCENT,cursor:'pointer'}}/>
                {g.label}
              </label>
            ))}
          </div>
          <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
            <button style={C.btnSecondary} onClick={resetGroups}>Reset defaults</button>
            <button style={C.btnPrimary} onClick={()=>setGroupModal(false)}>Done</button>
          </div>
        </Modal>
      )}

      {modal && (
        <Modal title={modal==='new'?'New Platform':`Edit: ${modal.name}`} onClose={()=>setModal(null)}>
          <div style={{ display:'flex', gap:2, marginBottom:20, borderBottom:'1px solid #dce8f0' }}>
            {['CPM & Pricing','Audience & Reach','Frequency','Format Info'].map(t=>(
              <button key={t} onClick={()=>setTab(t)} style={{
                padding:'6px 14px', fontSize:12, fontWeight:600, cursor:'pointer',
                background:'transparent', border:'none',
                borderBottom: tab===t ? `2px solid ${ACCENT}` : '2px solid transparent',
                color: tab===t ? ACCENT : '#8aa0b8',
              }}>{t}</button>
            ))}
          </div>

          {tab==='CPM & Pricing' && <>
            {f('name','Platform Name *','text')}
            {f('seller','Seller / Publisher','text')}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {f('base_cpm','Base CPM (€)',undefined,0.01)}
              {f('viewability','Viewability (0-1)',undefined,0.01)}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {f('ctr','CTR (0-1)',undefined,0.0001)}
              {f('vtr','VTR (0-1)',undefined,0.01)}
            </div>
            <label style={{display:'block', marginBottom:14}}>
              <span style={C.label}>CPC (€)</span>
              <input type="number" min={0} max={50} step={0.01} value={form.cpc??''} placeholder="No value"
                onChange={e=>setForm(p=>({...p,cpc:e.target.value===''?null:parseFloat(e.target.value)}))} style={C.input}/>
              <span style={{fontSize:11,color:'#8aa0b8'}}>Required for SEM platforms to be considered by automatic allocation. Max €50.</span>
            </label>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <label style={{display:'block', marginBottom:14}}>
                <span style={C.label}>Base Discount (0-0.90)</span>
                <input type="number" min={0} max={0.90} step={0.01} value={form.base_discount??''} placeholder="No value"
                  onChange={e=>setForm(p=>({...p,base_discount:e.target.value===''?null:parseFloat(e.target.value)}))} style={C.input}/>
              </label>
              <label style={{display:'block', marginBottom:14}}>
                <span style={C.label}>CVR (0-1)</span>
                <input type="number" min={0} max={1} step={0.001} value={form.cvr??''} placeholder="No value"
                  onChange={e=>setForm(p=>({...p,cvr:e.target.value===''?null:parseFloat(e.target.value)}))} style={C.input}/>
              </label>
            </div>
            <label style={{display:'block', marginBottom:14}}>
              <span style={C.label}>Engagement Rate (0-1)</span>
              <input type="number" min={0} max={1} step={0.001} value={form.engagement_rate??''} placeholder="No value"
                onChange={e=>setForm(p=>({...p,engagement_rate:e.target.value===''?null:parseFloat(e.target.value)}))} style={C.input}/>
              <span style={{fontSize:11,color:'#8aa0b8'}}>Optional on every platform (video formats can have one too, e.g. Instagram Reels). Leave blank if unset.</span>
            </label>
            <div>
              <span style={C.label}>Seasonal CPM (monthly multipliers — 1.0 = base price)</span>
              <div style={{overflowX:'auto'}}>
                <table className="out-table" style={{width:'100%'}}><thead><tr>{MONTHS.map(m=><th key={m} style={{minWidth:40,fontSize:10}}>{m}</th>)}</tr></thead>
                <tbody><tr>{(form.seasonal_cpm||Array(12).fill(1)).map((v,i)=>(
                  <td key={i}><input type="text" inputMode="decimal" value={v} style={{width:38,fontSize:10,textAlign:'center',border:'1px solid #c8d8e8',borderRadius:4,padding:2}}
                    onFocus={e=>e.target.select()}
                    onChange={e=>{const sc=[...(form.seasonal_cpm||Array(12).fill(1))];sc[i]=e.target.value;setForm(p=>({...p,seasonal_cpm:sc}));}}
                    onBlur={e=>{const sc=[...(form.seasonal_cpm||Array(12).fill(1))];const v2=parseFloat(e.target.value);sc[i]=isNaN(v2)||v2<=0?1:Math.round(v2*100)/100;setForm(p=>({...p,seasonal_cpm:sc}));}}/></td>
                ))}</tr></tbody></table>
              </div>
            </div>
          </>}

          {tab==='Audience & Reach' && <>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {f('audience_comp','Audience Comp (0-1)',undefined,0.01)}
              {f('real_users_coef','Real Users Coef',undefined,0.1)}
            </div>
            <label style={{display:'block', marginBottom:14}}>
              <span style={C.label}>Reach Pool</span>
              <select value={form.reach_pool} disabled={clientRO('reach_pool')} onChange={e=>setForm(p=>({...p,reach_pool:e.target.value}))} style={{...C.input, ...(clientRO('reach_pool')?{background:'rgba(220,232,240,0.4)',color:'#8aa0b8',cursor:'not-allowed'}:{})}}>
                {['yt','social','video','display','other'].map(r=><option key={r} value={r}>{r}</option>)}
              </select>
            </label>
          </>}

          {tab==='Frequency' && <>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {f('weekly_frq','Weekly FRQ (avg/wk)',undefined,0.5)}
              <label style={{display:'block', marginBottom:14}}>
                <span style={C.label}>Monthly FRQ (auto)</span>
                <input type="text" value={`${form.weekly_frq} avg/wk`} readOnly style={{...C.input, background:'rgba(220,232,240,0.4)', color:'#8aa0b8', cursor:'not-allowed'}}/>
                <span style={{fontSize:11,color:'#8aa0b8'}}>Same weekly average applies each week of the month</span>
              </label>
            </div>
            {scope==='global' && <label style={{display:'block', marginBottom:14}}>
              <span style={C.label}>Max Weekly Frequency (cap)</span>
              <input type="number" min={1} max={50} step={1} value={form.max_weekly_frequency??''} placeholder="10 (default)"
                onChange={e=>setForm(p=>({...p,max_weekly_frequency:e.target.value===''?null:parseInt(e.target.value)||null}))} style={C.input}/>
              <span style={{fontSize:11,color:'#8aa0b8'}}>Planner warning threshold. Leave blank for default (10).</span>
            </label>}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <label style={{display:'block', marginBottom:14}}>
                <span style={C.label}>Weekly Impression Cap</span>
                <input type="number" min={0} step={10000} value={form.weekly_impression_cap??''} placeholder="No cap"
                  onChange={e=>setForm(p=>({...p,weekly_impression_cap:e.target.value===''?null:parseInt(e.target.value)||null}))} style={C.input}/>
              </label>
              <label style={{display:'block', marginBottom:14}}>
                <span style={C.label}>Monthly Impression Cap</span>
                <input type="number" min={0} step={10000} value={form.monthly_impression_cap??''} placeholder="No cap"
                  onChange={e=>setForm(p=>({...p,monthly_impression_cap:e.target.value===''?null:parseInt(e.target.value)||null}))} style={C.input}/>
              </label>
            </div>
            <span style={{fontSize:11,color:'#8aa0b8',display:'block',marginTop:-10,marginBottom:14}}>Leave blank for no cap. Weekly/monthly caps are the only volume limits.</span>
          </>}

          {tab==='Format Info' && <>
            <label style={{display:'block', marginBottom:14}}>
              <span style={C.label}>Ad Type</span>
              <select value={form.type} disabled={clientRO('type')} onChange={e=>setForm(p=>({...p,type:e.target.value}))} style={{...C.input, ...(clientRO('type')?{background:'rgba(220,232,240,0.4)',color:'#8aa0b8',cursor:'not-allowed'}:{})}}>
                {AD_TYPES.map(([k,l])=><option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            {f('format_group','Format Group','text')}
            {showPublisherCols && <>
              <label style={{display:'block', marginBottom:14}}>
                <span style={C.label}>Site (leave blank = External)</span>
                <select value={form.site_id||''} disabled={clientRO('site_id')} onChange={e=>setForm(p=>({...p,site_id:e.target.value?+e.target.value:null,device:e.target.value?p.device:'',format_overlap_group:e.target.value?p.format_overlap_group:''}))} style={{...C.input, ...(clientRO('site_id')?{background:'rgba(220,232,240,0.4)',color:'#8aa0b8',cursor:'not-allowed'}:{})}}>
                  <option value="">— External platform —</option>
                  {(sites||[]).map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
              {form.site_id && <>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <label style={{display:'block', marginBottom:14}}>
                    <span style={C.label}>Device</span>
                    <select value={form.device} disabled={clientRO('device')} onChange={e=>setForm(p=>({...p,device:e.target.value}))} style={{...C.input, ...(clientRO('device')?{background:'rgba(220,232,240,0.4)',color:'#8aa0b8',cursor:'not-allowed'}:{})}}>
                      <option value="">— select —</option>
                      <option value="desktop">desktop</option>
                      <option value="mobile">mobile</option>
                      <option value="app">app</option>
                    </select>
                  </label>
                  <label style={{display:'block', marginBottom:14}}>
                    <span style={C.label}>Overlap Group</span>
                    <select value={form.format_overlap_group} disabled={clientRO('format_overlap_group')} onChange={e=>setForm(p=>({...p,format_overlap_group:e.target.value}))} style={{...C.input, ...(clientRO('format_overlap_group')?{background:'rgba(220,232,240,0.4)',color:'#8aa0b8',cursor:'not-allowed'}:{})}}>
                      <option value="">— select —</option>
                      <option value="desktop_banners">desktop_banners</option>
                      <option value="desktop_takeover">desktop_takeover</option>
                      <option value="mobile">mobile</option>
                      <option value="app">app</option>
                    </select>
                  </label>
                </div>
              </>}
            </>}
          </>}

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8 }}>
            <button style={C.btnSecondary} onClick={()=>setModal(null)}>Cancel</button>
            <button style={C.btnPrimary} onClick={handleSave} disabled={saving}>{saving?'Saving…':modal==='new'?'Create':'Save'}</button>
          </div>
        </Modal>
      )}

      {deactivateTarget && (
        <DeactivateDialog
          platform={deactivateTarget}
          isSuperAdmin={isSuperAdmin}
          onClose={()=>setDeactivateTarget(null)}
          onConfirm={async (p, force) => { await onDeactivate(p, force); onReload(); }}
        />
      )}
    </div>
  );
}
